import { Logger } from '@suntex.core/logging';

const logger = Logger.for(() => ExpiringTokenCache);

/**
 * Configuration for an {@link ExpiringTokenCache}.
 */
export interface ExpiringTokenCacheOptions<T> {
  /**
   * Fetches a fresh token. Called when no token is cached, the cached token has
   * expired, or a background refresh of a stale-but-valid token is triggered.
   */
  fetch: () => Promise<T>;

  /**
   * Returns the absolute expiry of a token as epoch milliseconds, or `null` when
   * the token carries no expiry (in which case it is treated as non-expiring).
   * May throw for malformed tokens; a throw is treated the same as `null`.
   */
  getExpiryMs: (value: T) => number | null;

  /**
   * How long before expiry a still-valid token is considered stale and eligible
   * for a background refresh.
   */
  refreshThresholdMs: number;

  /**
   * Minimum delay between background refresh attempts. Prevents hammering the
   * fetch when a freshly issued token is itself already within the stale window.
   */
  refreshCooldownMs: number;
}

/**
 * Caches a single expiring token and keeps it fresh.
 *
 * A call to {@link get} returns the cached token while it is valid. When the
 * cached token is within {@link ExpiringTokenCacheOptions.refreshThresholdMs} of
 * expiry it is still returned immediately, but a background refresh is kicked off
 * so the next caller gets a newer one - the "stale but not expired triggers
 * refresh" pattern. When there is no token, or the cached token has expired, the
 * call awaits a fresh fetch instead.
 *
 * Concurrent fetches are de-duplicated, and background refreshes are throttled by
 * {@link ExpiringTokenCacheOptions.refreshCooldownMs}.
 */
export class ExpiringTokenCache<T> {
  private current: T | null = null;
  private inFlight: Promise<T> | null = null;
  private lastRefreshAttemptMs = 0;

  constructor(private readonly options: ExpiringTokenCacheOptions<T>) {}

  /**
   * Returns a valid token, fetching one if necessary. Resolves with the cached
   * token when it is still valid (triggering a background refresh if it is
   * stale), otherwise awaits and resolves with a freshly fetched token.
   */
  async get(): Promise<T> {
    const value = this.current;
    if (value !== null && !this.isExpired(value)) {
      this.maybeBackgroundRefresh(value);
      return value;
    }
    return this.fetchAndStore();
  }

  private fetchAndStore(): Promise<T> {
    if (this.inFlight) {
      return this.inFlight;
    }

    const promise = this.options
      .fetch()
      .then((value) => {
        this.current = value;
        return value;
      })
      .catch((e: unknown) => {
        logger.warn('Token refresh failed.', {}, e);
        throw e;
      })
      .finally(() => {
        this.inFlight = null;
      });

    this.inFlight = promise;
    return promise;
  }

  private maybeBackgroundRefresh(value: T): void {
    if (this.inFlight || !this.isStale(value)) {
      return;
    }
    const now = Date.now();
    if (now - this.lastRefreshAttemptMs < this.options.refreshCooldownMs) {
      return;
    }
    this.lastRefreshAttemptMs = now;
    // Fire and forget: failures are logged inside fetchAndStore, and the stale
    // (but still valid) token remains cached for callers until it expires.
    void this.fetchAndStore().catch(() => undefined);
  }

  private isExpired(value: T): boolean {
    const expiryMs = this.safeGetExpiryMs(value);
    if (expiryMs === null) {
      return false;
    }
    return expiryMs <= Date.now();
  }

  private isStale(value: T): boolean {
    const expiryMs = this.safeGetExpiryMs(value);
    if (expiryMs === null) {
      return false;
    }
    // Only ever called for a token that get() has already found to be unexpired,
    // so a "not yet expired" check here would be redundant (and unreachable).
    return expiryMs - this.options.refreshThresholdMs <= Date.now();
  }

  private safeGetExpiryMs(value: T): number | null {
    try {
      return this.options.getExpiryMs(value);
    } catch {
      return null;
    }
  }
}
